import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Clock, User, CheckCircle } from 'lucide-react';

const HowToGuides: React.FC = () => {
  const guides = [
    {
      id: 1,
      title: "How to Build a Gaming PC in 2025",
      description: "Complete step-by-step guide to assembling your dream gaming rig with the latest components.",
      image: "https://images.unsplash.com/photo-1587202372634-32705e3bf49c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      duration: "45 min",
      difficulty: "Intermediate",
      steps: 12,
      category: "Hardware"
    },
    {
      id: 2,
      title: "Optimizing Windows for Gaming Performance",
      description: "Maximize your system's potential with advanced tweaks and optimization techniques.",
      image: "https://images.unsplash.com/photo-1593640408182-31c70c8268f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      duration: "30 min",
      difficulty: "Beginner",
      steps: 8,
      category: "Software"
    },
    {
      id: 3,
      title: "Configuring a Dual-Monitor Setup for Streamers",
      description: "Professional streaming setup with optimal display configuration and software integration.",
      image: "https://images.unsplash.com/photo-1547082299-de196ea013d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      duration: "25 min",
      difficulty: "Advanced",
      steps: 10,
      category: "Streaming"
    },
    {
      id: 4,
      title: "Custom RGB Lighting Setup Guide",
      description: "Create stunning synchronized lighting effects across all your gaming peripherals.",
      image: "https://images.unsplash.com/photo-1541746972996-4e0b0f93e586?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      duration: "20 min",
      difficulty: "Intermediate",
      steps: 6,
      category: "Aesthetics"
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-400 bg-green-400/20';
      case 'Intermediate': return 'text-yellow-400 bg-yellow-400/20';
      case 'Advanced': return 'text-red-400 bg-red-400/20';
      default: return 'text-gray-400 bg-gray-400/20';
    }
  };

  return (
    <section id="how-to" className="py-20 bg-dark-900">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-futuristic font-bold mb-6">
            <span className="text-white">How-To </span>
            <span className="bg-gradient-to-r from-primary-400 to-electric-500 bg-clip-text text-transparent">
              Guides
            </span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Master gaming technology with our comprehensive step-by-step tutorials
          </p>
        </motion.div>

        {/* Guides Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 via-electric-500 to-primary-500 transform md:-translate-x-0.5"></div>

          {guides.map((guide, index) => (
            <motion.div
              key={guide.id}
              className={`relative flex items-center mb-12 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              }`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              {/* Timeline Node */}
              <div className="absolute left-4 md:left-1/2 w-4 h-4 bg-primary-500 rounded-full transform md:-translate-x-2 z-10 animate-glow">
                <div className="absolute inset-0 bg-primary-400 rounded-full animate-ping"></div>
              </div>

              {/* Guide Card */}
              <motion.div
                className={`ml-12 md:ml-0 md:w-5/12 ${
                  index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                }`}
                whileHover={{ scale: 1.02, y: -5 }}
              >
                <div className="glass rounded-xl overflow-hidden group cursor-pointer">
                  {/* Card Image */}
                  <div className="relative overflow-hidden">
                    <img
                      src={guide.image}
                      alt={guide.title}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent" />
                    
                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 bg-primary-500/90 rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-white ml-1" />
                      </div>
                    </div>

                    {/* Category Badge */}
                    <div className="absolute top-4 right-4">
                      <span className="glass px-3 py-1 text-sm font-medium text-primary-400 rounded-full">
                        {guide.category}
                      </span>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-primary-400 transition-colors">
                      {guide.title}
                    </h3>
                    <p className="text-gray-300 mb-4 leading-relaxed">
                      {guide.description}
                    </p>

                    {/* Guide Meta Info */}
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <div className="flex items-center space-x-1 text-gray-400">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{guide.duration}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-400">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-sm">{guide.steps} steps</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(guide.difficulty)}`}>
                        {guide.difficulty}
                      </span>
                    </div>

                    {/* Start Guide Button */}
                    <motion.button
                      className="flex items-center space-x-2 text-primary-500 hover:text-primary-400 font-semibold group/btn"
                      whileHover={{ x: 5 }}
                    >
                      <span>Start Guide</span>
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>

              {/* Step Number (visible on larger screens) */}
              <div className={`hidden md:block absolute ${
                index % 2 === 0 ? 'right-4' : 'left-4'
              } top-1/2 transform -translate-y-1/2`}>
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-electric-500 rounded-full flex items-center justify-center font-futuristic font-bold text-white">
                  {String(index + 1).padStart(2, '0')}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Guides Button */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <motion.button
            className="glass px-8 py-4 rounded-lg font-semibold text-white border-2 border-primary-500 hover:bg-primary-500 transition-all duration-300 flex items-center space-x-2 mx-auto"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <User className="w-5 h-5" />
            <span>Browse All Guides</span>
            <ArrowRight className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowToGuides;
